import { saveLanguage, setLanguage } from 'react-translate';

/* Shim in support for Java style translation locales, turning 'en_US' -> 'en-US'.
This is really unfortunate but I've not found any other means to support using Java style
locales in our file names while still playing nice with Int.NumberFormat */
const nativeNumberFormat = Intl.NumberFormat;
Intl.NumberFormat = (locales, ...args) => nativeNumberFormat(locales?.replace('_', '-'), ...args);

/* eslint-disable global-require */
/* eslint-disable camelcase */
const DEFAULT_LANGUAGE = 'en_US';
const defaultLanguageTranslations = process.env.NODE_ENV === 'development'
  ? require('json-loader!yaml-loader!../languages/en_US.yml')
  : {};
const globalLanguageTranslations = process.env.NODE_ENV === 'development'
  ? require('json-loader!yaml-loader!../languages/global.yml')
  : {};

// need this to do file name replacements
const KEY_TO_FILE_NAME = {
  en_US: 'en_US.json',
  es_MX: 'es_MX.json',
  es_ES: 'es_ES.json',
  fr_FR: 'fr_FR.json',
  pt_PT: 'pt_PT.json',
  pt_BR: 'pt_BR.json',
  zh_CN: 'zh_CN.json',
  ja_JP: 'ja_JP.json',
  ko_KP: 'ko_KP.json',
  ru_RU: 'ru_RU.json',
  de_DE: 'de_DE.json',
  ar_SA: 'ar_SA.json',
  he_IL: 'he_IL.json',
  pl_PL: 'pl_PL.json',
  it_IT: 'it_IT.json',
  fr_CA: 'fr_CA.json',
  nl_NL: 'nl_NL.json',
  sv_SE: 'sv_SE.json',
  ro_RO: 'ro_RO.json',
  tr_TR: 'tr_TR.json',
  vi_VI: 'vi_VI.json',
  id_ID: 'id_ID.json',
  th_TH: 'th_TH.json',
  zh_TW: 'zh_TW.json',
  nb_NO: 'nb_NO.json',
  da_DK: 'da_DK.json',
  fi_FI: 'fi_FI.json',
};

const developmentLanguageContext = process.env.NODE_ENV === 'development'
  ? require.context('json-loader!yaml-loader!../languages/', false, /\.yml$/, 'lazy')
  : null;

const devLanguageCache = {};

function moduleTranslation(moduleValue) {
  return moduleValue?.default || moduleValue;
}

function mergeGlobalTranslations(languageTranslations) {
  return angular.merge({}, moduleTranslation(globalLanguageTranslations), moduleTranslation(languageTranslations));
}

function importDevelopmentLanguageByKey(languageKey) {
  if (languageKey === DEFAULT_LANGUAGE) {
    return Promise.resolve(defaultLanguageTranslations);
  }

  const fileName = KEY_TO_FILE_NAME[languageKey];
  if (!fileName) {
    return Promise.reject(new Error(`Unsupported language: ${languageKey}`));
  }

  if (!developmentLanguageContext) {
    return Promise.reject(new Error(`Language context unavailable: ${languageKey}`));
  }

  const ymlFileName = `./${fileName.replace('.json', '.yml')}`;
  if (ymlFileName === './global.yml') {
    return Promise.resolve(globalLanguageTranslations);
  }

  return developmentLanguageContext(ymlFileName);
}

function loadDevelopmentTranslations(languageKey) {
  if (devLanguageCache[languageKey]) {
    return Promise.resolve(devLanguageCache[languageKey]);
  }

  if (languageKey === DEFAULT_LANGUAGE) {
    const mergedDefaultTranslations = mergeGlobalTranslations(defaultLanguageTranslations);
    devLanguageCache[languageKey] = mergedDefaultTranslations;
    return Promise.resolve(mergedDefaultTranslations);
  }

  return importDevelopmentLanguageByKey(languageKey)
    .then(moduleTranslation)
    .then(mergeGlobalTranslations)
    .then((translations) => {
      devLanguageCache[languageKey] = translations;
      return translations;
    });
}

// Replace the file names above with their revisioned versions from the
// manifest
if (process.env.NODE_ENV === 'production') {
  /* eslint-disable import/no-unresolved */
  // references a generated file that may not be available before compilation
  const manifest = require('./../dist/rev-manifest.json');
  Object.keys(KEY_TO_FILE_NAME).forEach((key) => {
    KEY_TO_FILE_NAME[key] = manifest[KEY_TO_FILE_NAME[key]];
  });
}

/* @ngInject */
export function $translateStaticFilesLoader($q, $http) {
  return function (options) {
    if (process.env.NODE_ENV === 'development') {
      return $q.when(loadDevelopmentTranslations(options.key))
        .then((translations) => {
          saveLanguage(options.key, translations);
          return translations;
        }, () => $q.reject(options.key));
    }

    const key = KEY_TO_FILE_NAME[options.key];

    return $http(angular.extend({
      url: [
        options.prefix,
        key,
      ].join(''),
      method: 'GET',
      params: '',
    }, options.$http))
      .then((result) => {
        if (result.data) {
          // Save the react runtime generated translations for each language
          saveLanguage(options.key, result.data);
        } else {
          // we were getting empty translation objects - https://sentry.io/organizations/novoed/issues/1730135047/events/e07520243ff044ae98a66e3363d4b0a6/
          throw Error('Language not loaded in react-translate but no languageJson was provided for', options.key);
        }

        return result.data;
      }, () => $q.reject(options.key));
  };
}

$translateStaticFilesLoader.displayName = '$translateStaticFilesLoader';

/* @ngInject */
export function configureTranslateProvider($translateProvider, config) {
  let $cookies;
  angular.injector(['ngCookies']).invoke(['$cookies', function (_$cookies) {
    $cookies = _$cookies;
  }]);
  const language = $cookies.get('platform_language') || config.defaultPlatformLanguage;
  $translateProvider.useMessageFormatInterpolation();
  $translateProvider.fallbackLanguage(config.defaultPlatformLanguage);
  $translateProvider.preferredLanguage(language);
  $translateProvider.useSanitizeValueStrategy(null);

  // In development, preload only en_US and lazy-load all non-default languages.
  if (process.env.NODE_ENV === 'development') {
    const defaultTranslations = mergeGlobalTranslations(defaultLanguageTranslations);
    $translateProvider.translations(DEFAULT_LANGUAGE, defaultTranslations);
    saveLanguage(DEFAULT_LANGUAGE, defaultTranslations);
    $translateProvider.useLoader('$translateStaticFilesLoader');
  } else {
    // Prod mode static file setup
    $translateProvider.useStaticFilesLoader({
      prefix: config.translation.staticUrl.prefix,
    });
  }

  setLanguage(language);

  $translateProvider.forceAsyncReload(true);
}
